// Puerto de packages/hce/src/hce/paciente/DatosDemograficosTab.tsx.
// Fase 2 (ver PLAN.md, "001 — campos nuevos de filiación"): agrega los campos del
// instructivo oficial del Anexo 1 que faltaban (tipo de documento, lugar de
// nacimiento, nacionalidad, autoidentificación étnica, nivel de educación, tipo de
// empresa, seguro de salud, tipo de bono, residencia estructurada con el catálogo de
// ubicación de Ecuador, representante legal si el paciente es menor de 5 años) — se
// capturan acá una sola vez; 001 (y más adelante 038) solo los leen.
import { useMedplum, useResource } from '@medplum/react-hooks';
import type { Patient } from '@medplum/fhirtypes';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { notify } from '@/components/ui/sonner';
import { cantonesDeProvincia, cantonPorNombre, parroquiaPorNombre, parroquiasDeCanton, PROVINCIAS_ECUADOR, provinciaPorNombre } from '@/lib/catalogoUbicacionEcuador';
import { reemplazarExtensiones } from '@/lib/extensionesFhir';
import { edadMsp } from '@logiacare/hce-pdf';
import {
  CODIGO_REPRESENTANTE_LEGAL,
  edadDe,
  EXT_AUTOIDENTIFICACION_ETNICA,
  EXT_LUGAR_NACIMIENTO,
  EXT_NACIONALIDAD,
  EXT_NACIONALIDAD_ETNICA,
  EXT_NIVEL_EDUCACION,
  EXT_ESTADO_EDUCACION,
  EXT_OCUPACION,
  EXT_PUEBLO,
  EXT_SEGURO_SALUD,
  EXT_TIPO_BONO,
  EXT_TIPO_CONTACTO_PACIENTE,
  EXT_TIPO_EMPRESA,
  URL_CODESYSTEM_AUTOIDENTIFICACION_ETNICA,
} from './filiacionCompleta';
import {
  AUTOIDENTIFICACION_ETNICA,
  direccionContacto,
  ESTADOS_CIVILES,
  ESTADOS_EDUCACION,
  NACIONALIDADES,
  NACIONALIDADES_ETNICAS,
  NIVELES_EDUCACION,
  nombreDesdePatient,
  nombreParaPatient,
  opcionesConAnterior,
  PUEBLOS,
  telecomParaPatient,
  telefonosDesdePatient,
  TIPOS_BONO,
  TIPOS_EMPRESA,
  type OpcionCatalogo,
} from './filiacionDatos';

const SISTEMA_CEDULA = 'http://logiacare.ec/fhir/identifier/cedula-ec';
/** N° de Historia Clínica Única (HCU) — Anexo 1 MSP, identifier propio (distinto de la cédula) que va en el encabezado de los 10 formularios MSP (`EncabezadoMspCampos.tsx`). */
export const SISTEMA_HCU = 'http://logiacare.ec/fhir/identifier/hcu-msp';
const SISTEMA_TIPO_DOCUMENTO = 'http://logiacare.ec/fhir/CodeSystem/tipo-documento-identificacion';

interface FormularioDemografico {
  primerNombre: string;
  segundoNombre: string;
  primerApellido: string;
  segundoApellido: string;
  identificacion: string;
  numeroHcu: string;
  tipoDocumento: string;
  birthDate: string;
  gender: string;
  estadoCivil: string;
  ocupacion: string;
  telefonoFijo: string;
  telefonoCelular: string;
  email: string;
  contactoNombre: string;
  contactoRelacion: string;
  contactoDireccion: string;
  contactoTelefono: string;
  lugarNacimiento: string;
  nacionalidad: string;
  autoidentificacionEtnica: string;
  nacionalidadEtnica: string;
  pueblo: string;
  nivelEducacion: string;
  estadoEducacion: string;
  tipoEmpresa: string;
  seguroSalud: string;
  tipoBono: string;
  provinciaCodigo: string;
  cantonCodigo: string;
  parroquiaCodigo: string;
  barrio: string;
  callePrincipal: string;
  calleSecundaria: string;
  referenciaDireccion: string;
  direccionLegadoSinEstructurar: string;
  representanteNombre: string;
  representanteDocumento: string;
  representanteTelefono: string;
}

function formularioDesdePatient(patient: Patient): FormularioDemografico {
  const direccion = patient.address?.[0];
  const provincia = provinciaPorNombre(direccion?.state);
  const canton = cantonPorNombre(provincia?.codigo, direccion?.district);
  const parroquia = parroquiaPorNombre(provincia?.codigo, canton?.codigo, direccion?.city);
  const contactoEmergencia = patient.contact?.find((c) => !c.extension?.some((e) => e.url === EXT_TIPO_CONTACTO_PACIENTE && e.valueCode === CODIGO_REPRESENTANTE_LEGAL));
  const contactoRepresentante = patient.contact?.find((c) => c.extension?.some((e) => e.url === EXT_TIPO_CONTACTO_PACIENTE && e.valueCode === CODIGO_REPRESENTANTE_LEGAL));
  const ext = patient.extension;

  return {
    ...nombreDesdePatient(patient),
    identificacion: patient.identifier?.find((i) => i.system === SISTEMA_CEDULA)?.value ?? patient.identifier?.[0]?.value ?? '',
    numeroHcu: patient.identifier?.find((i) => i.system === SISTEMA_HCU)?.value ?? '',
    tipoDocumento: patient.identifier?.[0]?.type?.coding?.find((c) => c.system === SISTEMA_TIPO_DOCUMENTO)?.code ?? '',
    birthDate: patient.birthDate ?? '',
    gender: patient.gender ?? '',
    estadoCivil: patient.maritalStatus?.text ?? '',
    ocupacion: ext?.find((e) => e.url === EXT_OCUPACION)?.valueString ?? '',
    telefonoFijo: telefonosDesdePatient(patient).fijo,
    telefonoCelular: telefonosDesdePatient(patient).celular,
    email: patient.telecom?.find((c) => c.system === 'email')?.value ?? '',
    contactoNombre: contactoEmergencia?.name?.text ?? '',
    contactoRelacion: contactoEmergencia?.relationship?.[0]?.text ?? '',
    contactoDireccion: contactoEmergencia?.address?.text ?? '',
    contactoTelefono: contactoEmergencia?.telecom?.find((c) => c.system === 'phone')?.value ?? '',
    lugarNacimiento: ext?.find((e) => e.url === EXT_LUGAR_NACIMIENTO)?.valueAddress?.text ?? '',
    nacionalidad: ext?.find((e) => e.url === EXT_NACIONALIDAD)?.valueString ?? 'Ecuatoriana',
    autoidentificacionEtnica: ext?.find((e) => e.url === EXT_AUTOIDENTIFICACION_ETNICA)?.valueCodeableConcept?.coding?.[0]?.code ?? '',
    nacionalidadEtnica: ext?.find((e) => e.url === EXT_NACIONALIDAD_ETNICA)?.valueString ?? '',
    pueblo: ext?.find((e) => e.url === EXT_PUEBLO)?.valueString ?? '',
    nivelEducacion: ext?.find((e) => e.url === EXT_NIVEL_EDUCACION)?.valueString ?? '',
    estadoEducacion: ext?.find((e) => e.url === EXT_ESTADO_EDUCACION)?.valueString ?? '',
    tipoEmpresa: ext?.find((e) => e.url === EXT_TIPO_EMPRESA)?.valueString ?? '',
    seguroSalud: ext?.find((e) => e.url === EXT_SEGURO_SALUD)?.valueString ?? '',
    tipoBono: ext?.find((e) => e.url === EXT_TIPO_BONO)?.valueString ?? '',
    provinciaCodigo: provincia?.codigo ?? '',
    cantonCodigo: canton?.codigo ?? '',
    parroquiaCodigo: parroquia?.codigo ?? '',
    barrio: direccion?.line?.[0] ?? '',
    callePrincipal: direccion?.line?.[1] ?? '',
    calleSecundaria: direccion?.line?.[2] ?? '',
    referenciaDireccion: direccion?.line?.[3] ?? '',
    direccionLegadoSinEstructurar: !provincia && direccion?.text ? direccion.text : '',
    representanteNombre: contactoRepresentante?.name?.text ?? '',
    representanteDocumento: contactoRepresentante?.telecom?.find((c) => c.system === 'other')?.value ?? '',
    representanteTelefono: contactoRepresentante?.telecom?.find((c) => c.system === 'phone')?.value ?? '',
  };
}

export function DatosDemograficosTab(): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const { id } = useParams() as { id: string };
  const patient = useResource<Patient>({ reference: `Patient/${id}` });
  const [form, setForm] = useState<FormularioDemografico>();
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (patient) setForm(formularioDesdePatient(patient));
  }, [patient]);

  const campo = (clave: keyof FormularioDemografico) => ({
    value: form?.[clave] ?? '',
    onChange: (e: { currentTarget: { value: string } }) => setForm((f) => (f ? { ...f, [clave]: e.currentTarget.value } : f)),
  });

  const guardar = async (): Promise<void> => {
    if (!patient || !form) return;
    setGuardando(true);
    try {
      const otrosIdentificadores = (patient.identifier ?? []).filter((i) => i.system !== SISTEMA_CEDULA && i.system !== SISTEMA_HCU);
      const telecom = telecomParaPatient(patient.telecom, { fijo: form.telefonoFijo, celular: form.telefonoCelular }, form.email);

      const provincia = PROVINCIAS_ECUADOR.find((p) => p.codigo === form.provinciaCodigo);
      const canton = cantonesDeProvincia(form.provinciaCodigo).find((c) => c.codigo === form.cantonCodigo);
      const parroquia = parroquiasDeCanton(form.provinciaCodigo, form.cantonCodigo).find((p) => p.codigo === form.parroquiaCodigo);
      const lineas = [form.barrio, form.callePrincipal, form.calleSecundaria, form.referenciaDireccion].map((v) => v.trim());
      const hayResidenciaEstructurada = provincia && canton;
      const textoResidencia = hayResidenciaEstructurada
        ? [parroquia?.nombre, canton!.nombre, provincia!.nombre, ...lineas.filter(Boolean)].filter(Boolean).join(', ')
        : form.direccionLegadoSinEstructurar || undefined;

      const hayContactoEmergencia = form.contactoNombre.trim() || form.contactoTelefono.trim() || form.contactoRelacion.trim() || form.contactoDireccion.trim();
      const edad = edadDe(form.birthDate);
      const hayRepresentante = edad !== undefined && edad < 5 && (form.representanteNombre.trim() || form.representanteDocumento.trim() || form.representanteTelefono.trim());

      await medplum.updateResource<Patient>({
        ...patient,
        name: [nombreParaPatient(form)],
        identifier: [
          ...otrosIdentificadores,
          ...(form.identificacion.trim()
            ? [{ system: SISTEMA_CEDULA, value: form.identificacion.trim(), type: form.tipoDocumento ? { coding: [{ system: SISTEMA_TIPO_DOCUMENTO, code: form.tipoDocumento }] } : undefined }]
            : []),
          ...(form.numeroHcu.trim() ? [{ system: SISTEMA_HCU, value: form.numeroHcu.trim() }] : []),
        ],
        birthDate: form.birthDate || undefined,
        gender: (form.gender || undefined) as Patient['gender'],
        maritalStatus: form.estadoCivil ? { text: form.estadoCivil } : undefined,
        address: textoResidencia
          ? [
              {
                text: textoResidencia,
                state: provincia?.nombre,
                district: canton?.nombre,
                city: parroquia?.nombre,
                line: lineas.some(Boolean) ? lineas : undefined,
              },
            ]
          : undefined,
        telecom: telecom.length ? telecom : undefined,
        contact: [
          ...(hayContactoEmergencia
            ? [
                {
                  relationship: form.contactoRelacion.trim() ? [{ text: form.contactoRelacion.trim() }] : undefined,
                  name: form.contactoNombre.trim() ? { text: form.contactoNombre.trim() } : undefined,
                  address: direccionContacto(form.contactoDireccion),
                  telecom: form.contactoTelefono.trim() ? [{ system: 'phone' as const, value: form.contactoTelefono.trim() }] : undefined,
                },
              ]
            : []),
          ...(hayRepresentante
            ? [
                {
                  extension: [{ url: EXT_TIPO_CONTACTO_PACIENTE, valueCode: CODIGO_REPRESENTANTE_LEGAL }],
                  relationship: [{ text: t('fichaPaciente.demograficos.representanteLegalTitulo') }],
                  name: form.representanteNombre.trim() ? { text: form.representanteNombre.trim() } : undefined,
                  telecom: [
                    ...(form.representanteTelefono.trim() ? [{ system: 'phone' as const, value: form.representanteTelefono.trim() }] : []),
                    ...(form.representanteDocumento.trim() ? [{ system: 'other' as const, value: form.representanteDocumento.trim() }] : []),
                  ],
                },
              ]
            : []),
        ],
        extension: reemplazarExtensiones(patient.extension, [
          { url: EXT_OCUPACION, extension: form.ocupacion.trim() ? { url: EXT_OCUPACION, valueString: form.ocupacion.trim() } : undefined },
          { url: EXT_LUGAR_NACIMIENTO, extension: form.lugarNacimiento.trim() ? { url: EXT_LUGAR_NACIMIENTO, valueAddress: { text: form.lugarNacimiento.trim() } } : undefined },
          { url: EXT_NACIONALIDAD, extension: form.nacionalidad.trim() ? { url: EXT_NACIONALIDAD, valueString: form.nacionalidad.trim() } : undefined },
          {
            url: EXT_AUTOIDENTIFICACION_ETNICA,
            extension: form.autoidentificacionEtnica
              ? { url: EXT_AUTOIDENTIFICACION_ETNICA, valueCodeableConcept: { coding: [{ system: URL_CODESYSTEM_AUTOIDENTIFICACION_ETNICA, code: form.autoidentificacionEtnica }] } }
              : undefined,
          },
          { url: EXT_NACIONALIDAD_ETNICA, extension: form.nacionalidadEtnica.trim() ? { url: EXT_NACIONALIDAD_ETNICA, valueString: form.nacionalidadEtnica.trim() } : undefined },
          { url: EXT_PUEBLO, extension: form.pueblo.trim() ? { url: EXT_PUEBLO, valueString: form.pueblo.trim() } : undefined },
          { url: EXT_NIVEL_EDUCACION, extension: form.nivelEducacion ? { url: EXT_NIVEL_EDUCACION, valueString: form.nivelEducacion } : undefined },
          { url: EXT_ESTADO_EDUCACION, extension: form.estadoEducacion ? { url: EXT_ESTADO_EDUCACION, valueString: form.estadoEducacion } : undefined },
          { url: EXT_TIPO_EMPRESA, extension: form.tipoEmpresa ? { url: EXT_TIPO_EMPRESA, valueString: form.tipoEmpresa } : undefined },
          { url: EXT_SEGURO_SALUD, extension: form.seguroSalud ? { url: EXT_SEGURO_SALUD, valueString: form.seguroSalud } : undefined },
          { url: EXT_TIPO_BONO, extension: form.tipoBono ? { url: EXT_TIPO_BONO, valueString: form.tipoBono } : undefined },
        ]),
      });
      notify({ color: 'green', message: t('questionnaireEngine.guardadoOk') });
    } catch (err) {
      notify({ color: 'red', message: err instanceof Error ? err.message : String(err), autoClose: false });
    }
    setGuardando(false);
  };

  if (!form) return <></>;

  const edad = edadDe(form.birthDate);
  const edadConCondicion = edadMsp(form.birthDate || undefined);
  const opcionesTipoDocumento = t('fichaPaciente.demograficos.tipoDocumentoOpciones', { returnObjects: true }) as Record<string, string>;
  const opcionesSeguroSalud = t('fichaPaciente.demograficos.seguroSaludOpciones', { returnObjects: true }) as Record<string, string>;

  function Campo({ label, children }: { label: string; children: JSX.Element }): JSX.Element {
    return (
      <div>
        <label className="mb-1 block text-sm font-medium">{label}</label>
        {children}
      </div>
    );
  }

  function CampoSelect({ label, valor, opciones, onChange }: { label: string; valor: string; opciones: Record<string, string> | readonly OpcionCatalogo[]; onChange: (v: string) => void }): JSX.Element {
    const lista: readonly OpcionCatalogo[] = Array.isArray(opciones) ? (opciones as readonly OpcionCatalogo[]) : Object.entries(opciones as Record<string, string>).map(([codigo, etiqueta]) => ({ codigo, etiqueta }));
    return (
      <Campo label={label}>
        <Select value={valor || undefined} onValueChange={onChange}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {lista.map(({ codigo, etiqueta }) => (
              <SelectItem key={codigo} value={codigo}>
                {etiqueta}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Campo>
    );
  }

  const cantones = cantonesDeProvincia(form.provinciaCodigo);
  const parroquias = parroquiasDeCanton(form.provinciaCodigo, form.cantonCodigo);

  return (
    <div className="flex flex-col gap-5">
      <h5 className="text-sm font-semibold">{t('fichaPaciente.demograficos.tituloObligatorio')}</h5>
      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-12 sm:col-span-3">
          <Campo label={t('fichaPaciente.demograficos.primerApellido')}>
            <Input {...campo('primerApellido')} />
          </Campo>
        </div>
        <div className="col-span-12 sm:col-span-3">
          <Campo label={t('fichaPaciente.demograficos.segundoApellido')}>
            <Input {...campo('segundoApellido')} />
          </Campo>
        </div>
        <div className="col-span-12 sm:col-span-3">
          <Campo label={t('fichaPaciente.demograficos.primerNombre')}>
            <Input {...campo('primerNombre')} />
          </Campo>
        </div>
        <div className="col-span-12 sm:col-span-3">
          <Campo label={t('fichaPaciente.demograficos.segundoNombre')}>
            <Input {...campo('segundoNombre')} />
          </Campo>
        </div>
        <div className="col-span-12 sm:col-span-4">
          <CampoSelect label={t('fichaPaciente.demograficos.tipoDocumento')} valor={form.tipoDocumento} opciones={opcionesTipoDocumento} onChange={(v) => setForm((f) => (f ? { ...f, tipoDocumento: v } : f))} />
        </div>
        <div className="col-span-12 sm:col-span-4">
          <Campo label={t('fichaPaciente.demograficos.identificacionLabel')}>
            <Input {...campo('identificacion')} />
          </Campo>
        </div>
        <div className="col-span-12 sm:col-span-4">
          <Campo label={t('fichaPaciente.demograficos.numeroHcuLabel')}>
            <Input {...campo('numeroHcu')} />
          </Campo>
        </div>
        <div className="col-span-6 sm:col-span-3">
          <Campo label={t('fichaPaciente.sexo')}>
            <Select value={form.gender || undefined} onValueChange={(v) => setForm((f) => (f ? { ...f, gender: v } : f))}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {['male', 'female', 'other', 'unknown'].map((g) => (
                  <SelectItem key={g} value={g}>
                    {t(`fichaPaciente.genero.${g}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Campo>
        </div>
        <div className="col-span-6 sm:col-span-3">
          <Campo label={t('fichaPaciente.demograficos.fechaNacimiento')}>
            <Input type="date" {...campo('birthDate')} />
          </Campo>
        </div>
        <div className="col-span-3 sm:col-span-1">
          <Campo label={t('fichaPaciente.demograficos.edad')}>
            <Input value={edadConCondicion ? String(edadConCondicion.valor) : ''} disabled />
          </Campo>
        </div>
        <div className="col-span-3 sm:col-span-2">
          <Campo label={t('fichaPaciente.demograficos.condicionEdad')}>
            <Input value={edadConCondicion ? t(`fichaPaciente.demograficos.condicionEdadOpciones.${edadConCondicion.unidad}`) : ''} disabled />
          </Campo>
        </div>
        <div className="col-span-12 sm:col-span-4">
          <CampoSelect label={t('fichaPaciente.demograficos.estadoCivil')} valor={form.estadoCivil} opciones={opcionesConAnterior(ESTADOS_CIVILES, form.estadoCivil)} onChange={(v) => setForm((f) => (f ? { ...f, estadoCivil: v } : f))} />
        </div>
        <div className="col-span-12 sm:col-span-6">
          <Campo label={t('fichaPaciente.demograficos.lugarNacimiento')}>
            <Input {...campo('lugarNacimiento')} />
          </Campo>
        </div>
        <div className="col-span-12 sm:col-span-6">
          <CampoSelect label={t('fichaPaciente.demograficos.nacionalidad')} valor={form.nacionalidad} opciones={opcionesConAnterior(NACIONALIDADES, form.nacionalidad)} onChange={(v) => setForm((f) => (f ? { ...f, nacionalidad: v } : f))} />
        </div>
        <div className="col-span-12 sm:col-span-8">
          <Campo label={t('fichaPaciente.demograficos.ocupacion')}>
            <Input {...campo('ocupacion')} />
          </Campo>
        </div>
        <div className="col-span-12 sm:col-span-6">
          <Campo label={t('fichaPaciente.demograficos.telefonoFijo')}>
            <Input {...campo('telefonoFijo')} />
          </Campo>
        </div>
        <div className="col-span-12 sm:col-span-6">
          <Campo label={t('fichaPaciente.demograficos.telefonoCelular')}>
            <Input {...campo('telefonoCelular')} />
          </Campo>
        </div>
        <div className="col-span-12 sm:col-span-6">
          <Campo label={t('fichaPaciente.demograficos.email')}>
            <Input {...campo('email')} />
          </Campo>
        </div>
      </div>

      <h5 className="text-sm font-semibold">{t('fichaPaciente.demograficos.autoidentificacionEtnica')}</h5>
      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-12 sm:col-span-4">
          <CampoSelect
            label={t('fichaPaciente.demograficos.autoidentificacionEtnica')}
            valor={form.autoidentificacionEtnica}
            opciones={opcionesConAnterior(AUTOIDENTIFICACION_ETNICA, form.autoidentificacionEtnica)}
            onChange={(v) => setForm((f) => (f ? { ...f, autoidentificacionEtnica: v } : f))}
          />
        </div>
        {form.autoidentificacionEtnica === 'indigena' && (
          <div className="col-span-12 sm:col-span-4">
            <CampoSelect label={t('fichaPaciente.demograficos.nacionalidadEtnica')} valor={form.nacionalidadEtnica} opciones={opcionesConAnterior(NACIONALIDADES_ETNICAS, form.nacionalidadEtnica)} onChange={(v) => setForm((f) => (f ? { ...f, nacionalidadEtnica: v, pueblo: v === 'Kichwa' ? f.pueblo : '' } : f))} />
          </div>
        )}
        {form.nacionalidadEtnica.trim().toLowerCase() === 'kichwa' && (
          <div className="col-span-12 sm:col-span-4">
            <CampoSelect label={t('fichaPaciente.demograficos.pueblo')} valor={form.pueblo} opciones={opcionesConAnterior(PUEBLOS, form.pueblo)} onChange={(v) => setForm((f) => (f ? { ...f, pueblo: v } : f))} />
          </div>
        )}
      </div>

      <h5 className="text-sm font-semibold">{t('fichaPaciente.demograficos.nivelEducacion')}</h5>
      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-12 sm:col-span-4">
          <CampoSelect label={t('fichaPaciente.demograficos.nivelEducacion')} valor={form.nivelEducacion} opciones={opcionesConAnterior(NIVELES_EDUCACION, form.nivelEducacion)} onChange={(v) => setForm((f) => (f ? { ...f, nivelEducacion: v } : f))} />
        </div>
        <div className="col-span-12 sm:col-span-4">
          <CampoSelect label={t('fichaPaciente.demograficos.estadoEducacion')} valor={form.estadoEducacion} opciones={opcionesConAnterior(ESTADOS_EDUCACION, form.estadoEducacion)} onChange={(v) => setForm((f) => (f ? { ...f, estadoEducacion: v } : f))} />
        </div>
        <div className="col-span-12 sm:col-span-4">
          <CampoSelect label={t('fichaPaciente.demograficos.tipoEmpresa')} valor={form.tipoEmpresa} opciones={opcionesConAnterior(TIPOS_EMPRESA, form.tipoEmpresa)} onChange={(v) => setForm((f) => (f ? { ...f, tipoEmpresa: v } : f))} />
        </div>
        <div className="col-span-12 sm:col-span-6">
          <CampoSelect label={t('fichaPaciente.demograficos.seguroSalud')} valor={form.seguroSalud} opciones={opcionesSeguroSalud} onChange={(v) => setForm((f) => (f ? { ...f, seguroSalud: v } : f))} />
        </div>
        <div className="col-span-12 sm:col-span-6">
          <CampoSelect label={t('fichaPaciente.demograficos.tipoBono')} valor={form.tipoBono} opciones={opcionesConAnterior(TIPOS_BONO, form.tipoBono)} onChange={(v) => setForm((f) => (f ? { ...f, tipoBono: v } : f))} />
        </div>
      </div>

      <h5 className="text-sm font-semibold">{t('fichaPaciente.demograficos.residencia')}</h5>
      {form.direccionLegadoSinEstructurar && (
        <div className="rounded-md border border-amber-400 bg-amber-50 p-3 text-sm">
          <p className="font-medium">{t('fichaPaciente.demograficos.direccionSinEstructurar')}</p>
          <p className="text-hce-muted">{form.direccionLegadoSinEstructurar}</p>
        </div>
      )}
      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-12 sm:col-span-4">
          <Campo label={t('fichaPaciente.demograficos.provincia')}>
            <Select
              value={form.provinciaCodigo || undefined}
              onValueChange={(v) => setForm((f) => (f ? { ...f, provinciaCodigo: v, cantonCodigo: '', parroquiaCodigo: '' } : f))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PROVINCIAS_ECUADOR.map((p) => (
                  <SelectItem key={p.codigo} value={p.codigo}>
                    {p.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Campo>
        </div>
        <div className="col-span-12 sm:col-span-4">
          <Campo label={t('fichaPaciente.demograficos.canton')}>
            <Select
              value={form.cantonCodigo || undefined}
              onValueChange={(v) => setForm((f) => (f ? { ...f, cantonCodigo: v, parroquiaCodigo: '' } : f))}
              disabled={!form.provinciaCodigo}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {cantones.map((c) => (
                  <SelectItem key={c.codigo} value={c.codigo}>
                    {c.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Campo>
        </div>
        <div className="col-span-12 sm:col-span-4">
          <Campo label={t('fichaPaciente.demograficos.parroquia')}>
            <Select value={form.parroquiaCodigo || undefined} onValueChange={(v) => setForm((f) => (f ? { ...f, parroquiaCodigo: v } : f))} disabled={!form.cantonCodigo}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {parroquias.map((p) => (
                  <SelectItem key={p.codigo} value={p.codigo}>
                    {p.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Campo>
        </div>
        <div className="col-span-12 sm:col-span-3">
          <Campo label={t('fichaPaciente.demograficos.barrio')}>
            <Input {...campo('barrio')} />
          </Campo>
        </div>
        <div className="col-span-12 sm:col-span-3">
          <Campo label={t('fichaPaciente.demograficos.callePrincipal')}>
            <Input {...campo('callePrincipal')} />
          </Campo>
        </div>
        <div className="col-span-12 sm:col-span-3">
          <Campo label={t('fichaPaciente.demograficos.calleSecundaria')}>
            <Input {...campo('calleSecundaria')} />
          </Campo>
        </div>
        <div className="col-span-12 sm:col-span-3">
          <Campo label={t('fichaPaciente.demograficos.referenciaDireccion')}>
            <Input {...campo('referenciaDireccion')} />
          </Campo>
        </div>
      </div>

      {edad !== undefined && edad < 5 && (
        <>
          <h5 className="text-sm font-semibold">{t('fichaPaciente.demograficos.representanteLegalTitulo')}</h5>
          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-12 sm:col-span-4">
              <Campo label={t('fichaPaciente.demograficos.representanteNombre')}>
                <Input {...campo('representanteNombre')} />
              </Campo>
            </div>
            <div className="col-span-12 sm:col-span-4">
              <Campo label={t('fichaPaciente.demograficos.representanteDocumento')}>
                <Input {...campo('representanteDocumento')} />
              </Campo>
            </div>
            <div className="col-span-12 sm:col-span-4">
              <Campo label={t('fichaPaciente.demograficos.representanteTelefono')}>
                <Input {...campo('representanteTelefono')} />
              </Campo>
            </div>
          </div>
        </>
      )}

      <h5 className="text-sm font-semibold">{t('fichaPaciente.demograficos.contactoEmergenciaTitulo')}</h5>
      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-12 sm:col-span-4">
          <Campo label={t('fichaPaciente.demograficos.contactoNombre')}>
            <Input {...campo('contactoNombre')} />
          </Campo>
        </div>
        <div className="col-span-12 sm:col-span-4">
          <Campo label={t('fichaPaciente.demograficos.contactoRelacion')}>
            <Input {...campo('contactoRelacion')} />
          </Campo>
        </div>
        <div className="col-span-12 sm:col-span-6">
          <Campo label={t('fichaPaciente.demograficos.contactoDireccion')}>
            <Input {...campo('contactoDireccion')} />
          </Campo>
        </div>
        <div className="col-span-12 sm:col-span-6">
          <Campo label={t('fichaPaciente.demograficos.contactoTelefono')}>
            <Input {...campo('contactoTelefono')} />
          </Campo>
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={() => void guardar()} disabled={guardando}>
          {t('fichaPaciente.demograficos.guardar')}
        </Button>
      </div>
    </div>
  );
}
